import ApiService from "./api.service";
import Cookies from "js-cookie";
import { onClearDiscountError } from "../store/actions/discountActions";

/**
 * Service to handle cheapest book discount application
 */
export const CheapestBookDiscountService = {
  /**
   * Apply a discount coupon with retry logic if cart is not immediately available
   * @param {string} couponCode - The discount code to apply
   * @param {object} user - The current user object from Redux store
   * @param {object} callbacks - Callback functions for different states
   * @param {function} callbacks.onSuccess - Called when discount is successfully applied
   * @param {function} callbacks.onError - Called when there's an error applying the discount
   * @param {function} callbacks.onLoading - Called to update loading state
   * @param {number} retryCount - Current retry attempt (used internally for recursion)
   * @param {number} maxRetries - Maximum number of retry attempts
   */
  applyDiscountWithRetry: (
    couponCode,
    user,
    callbacks = {},
    retryCount = 0,
    maxRetries = 5
  ) => {
    const { onSuccess, onError } = callbacks;

    // Get the cart UUID
    const cartUuid = user?.cart?.uuid || Cookies.get("cartId");

    if (cartUuid) {
      console.log(`Cart UUID found: ${cartUuid}, applying discount`);

      // Prepare data for the API call
      const data = {
        couponCode: couponCode,
      };

      // Call createPaymentIntentWithCoupon
      console.log(`Applying coupon code "${couponCode}" to cart "${cartUuid}"`);

      ApiService.createPaymentIntentWithCoupon(cartUuid, data)
        .then((paymentResponse) => {
          console.log(
            "Payment intent response received:",
            paymentResponse.data
          );

          // Check if the response indicates success or failure
          if (paymentResponse.data && paymentResponse.data.success === false) {
            // Handle the case where the API returns success: false
            console.error(
              "Discount application failed:",
              paymentResponse.data.message
            );

            // Execute error callback if provided with the message from the API
            if (onError) {
              onError(
                paymentResponse.data.message || "Error applying discount",
                true // discount should be considered failed
              );
            }
          } else {
            // Success case - discount applied successfully
            console.log(
              "Payment intent created successfully:",
              paymentResponse.data
            );

            // Execute success callback if provided
            if (onSuccess) {
              onSuccess(paymentResponse.data);
            }

            // Handle redirection if needed
            if (paymentResponse.data.url) {
              window.location.href = paymentResponse.data.url;
            }
          }
        })
        .catch((paymentError) => {
          console.error("Error applying coupon:", paymentError);

          // Try to extract message from error response if available
          let errorMessage = "Error applying discount";
          if (paymentError.response && paymentError.response.data) {
            errorMessage = paymentError.response.data.message || errorMessage;
          }

          // Execute error callback if provided
          if (onError) {
            onError(errorMessage, true); // discount failed

            // Optional: clear Redux state if discount is invalid
            if (errorMessage.toLowerCase().includes("already used")) {
              callbacks.dispatch?.(onClearDiscountError()); // if using Redux
            }
          }
        });
    } else {
      // No cart UUID yet, check if we should retry
      if (retryCount < maxRetries) {
        console.log(
          `No cart UUID available yet. Retrying in 1 second... (Attempt ${
            retryCount + 1
          }/${maxRetries})`
        );

        // Retry after a delay
        setTimeout(() => {
          CheapestBookDiscountService.applyDiscountWithRetry(
            couponCode,
            user,
            callbacks,
            retryCount + 1,
            maxRetries
          );
        }, 1000); // Wait 1 second before retrying
      } else {
        console.error("Max retries reached. No cart UUID available.");

        // Execute error callback if provided
        if (onError) {
          onError("Unable to apply discount: Cart not available");
        }
      }
    }
  },

  /**
   * Refresh a discount after cart changes
   * @param {string} couponCode - The discount code to refresh
   * @param {object} user - The current user object from Redux store
   * @param {object} callbacks - Callback functions for different states
   */
  refreshDiscount: (couponCode, user, callbacks = {}) => {
    // Use the same apply method but with different logging
    console.log("Refreshing discount after cart change");
    CheapestBookDiscountService.applyDiscountWithRetry(
      couponCode,
      user,
      callbacks,
      0,
      3
    );
  },
};

export default CheapestBookDiscountService;
